import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Post } from "@/types/posts";
import { PostOptionsSheet, PostOwnerOptionsSheet } from "../PostOptionsSheet";
import { usePostsStore } from "@/stores/posts";
import { useNotificationToast } from "@/components/ui/NotificationToast";

interface PostOptionsHandlerProps {
  post: Post;
  onLocalDelete?: (id: string) => void;
  children: (props: {
    isOptionsOpen: boolean;
    handleOptionsClick: (e: React.MouseEvent) => void;
    optionsSheet: React.ReactNode;
  }) => React.ReactNode;
}

export function PostOptionsHandler({
  post,
  onLocalDelete,
  children,
}: PostOptionsHandlerProps) {
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const navigate = useNavigate();
  const deletePost = usePostsStore((state) => state.deletePost);
  const { showToast } = useNotificationToast();
  const isOwnerByName = post.author.display_name === "Bạn";

  const handleOptionsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOptionsOpen(true);
  };

  const handleEditPost = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    navigate(`/edit-post/${post.id}`);
    setIsOptionsOpen(false);
  };

  const handleDeletePost = async () => {
    if (
      post.author.display_name === "Bạn" &&
      post.author.location === "Local"
    ) {
      const local = JSON.parse(localStorage.getItem("locuno_posts") || "[]");
      const newLocal = local.filter((p: { id: string }) => p.id !== post.id);
      localStorage.setItem("locuno_posts", JSON.stringify(newLocal));
      onLocalDelete?.(post.id);
      showToast("Đã xóa bài viết thành công!", "success");
      setIsOptionsOpen(false);
      return;
    }
    try {
      await deletePost(post.id);
      showToast("Đã xóa bài viết thành công!", "success");
    } catch {
      showToast("Xóa bài viết thất bại!", "error");
    }
    setIsOptionsOpen(false);
  };

  const optionsSheet = isOwnerByName ? (
    <PostOwnerOptionsSheet
      isOpen={isOptionsOpen}
      onClose={() => setIsOptionsOpen(false)}
      onEdit={handleEditPost}
      onDelete={handleDeletePost}
    />
  ) : (
    <PostOptionsSheet
      post={post}
      isOpen={isOptionsOpen}
      onClose={() => setIsOptionsOpen(false)}
    />
  );

  return (
    <>
      {children({
        isOptionsOpen,
        handleOptionsClick,
        optionsSheet,
      })}
    </>
  );
}
